// src/components/partners/RetailerForm.jsx
import React, { useState, useEffect } from 'react';
import { Form, Card, Alert, Row, Col, InputGroup, Button } from 'react-bootstrap';
import { Save, X, User, Mail, Phone, MapPin, FileText, DollarSign, BookOpen, Users } from 'lucide-react';
import useForm from '../../hooks/useForm';
import useAuth from '../../hooks/useAuth';
import { createRetailer, getRetailerById, updateRetailer } from '../../api/partner';
import { getUsersByRole } from '../../api/users';

const RetailerForm = ({ retailerId, onCancel, onSuccess, commercialId }) => {
    const { isAdmin, isManager, user } = useAuth();
    const isEditMode = !!retailerId;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [commercials, setCommercials] = useState([]);
    const [loadingCommercials, setLoadingCommercials] = useState(false);

    const canAssignCommercial = isAdmin || isManager;

    const initialValues = {
        name: '',
        email: '',
        phone: '',
        address: '',
        vatApplicable: false,
        creditLimit: '',
        businessType: '',
        notes: '',
        commercialUser: commercialId ? { id: commercialId } : null
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    useEffect(() => {
        if (canAssignCommercial) {
            fetchCommercials();
        }

        if (isEditMode) {
            fetchRetailerData();
        } else if (commercialId && !isEditMode) {
            // Si on crée un nouveau détaillant et qu'un ID de commercial est fourni
            setValues(prev => ({
                ...prev,
                commercialUser: { id: commercialId }
            }));
        }
    }, [retailerId, commercialId]);

    // Dans RetailerForm.jsx
    const fetchCommercials = async () => {
        try {
            setLoadingCommercials(true);
            // Utiliser "SALES_REP" au lieu de "COMMERCIAL" ou "ROLE_COMMERCIAL"
            const data = await getUsersByRole('SALES_REP');
            setCommercials(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Erreur lors du chargement des commerciaux', err);
        } finally {
            setLoadingCommercials(false);
        }
    };

    const fetchRetailerData = async () => {
        try {
            setLoading(true);
            const retailerData = await getRetailerById(retailerId);

            setValues({
                name: retailerData.name || '',
                email: retailerData.email || '',
                phone: retailerData.phone || '',
                address: retailerData.address || '',
                vatApplicable: retailerData.vatApplicable || false,
                creditLimit: retailerData.creditLimit ? retailerData.creditLimit.toString() : '',
                businessType: retailerData.businessType || '',
                notes: retailerData.notes || '',
                commercialUser: retailerData.commercialUser || null
            });
        } catch (err) {
            setError('Impossible de charger les données du détaillant. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = {
        name: {
            required: true,
            errorMessage: 'Le nom du détaillant est obligatoire'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Veuillez saisir une adresse email valide'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Valider le formulaire
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const retailerData = {
                ...values,
                creditLimit: values.creditLimit ? parseFloat(values.creditLimit) : null
            };

            if (isEditMode) {
                // Mettre à jour le détaillant existant
                await updateRetailer(retailerId, retailerData);
                setSuccess('Détaillant mis à jour avec succès !');
            } else {
                // Créer un nouveau détaillant
                await createRetailer(retailerData);
                setSuccess('Détaillant créé avec succès !');
            }

            // Appeler le callback de succès après un court délai
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);

        } catch (err) {
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setValues(prev => ({ ...prev, [name]: checked }));
    };

    const handleCommercialChange = (e) => {
        const selectedCommercialId = e.target.value;
        if (selectedCommercialId) {
            setValues(prev => ({
                ...prev,
                commercialUser: { id: selectedCommercialId }
            }));
        } else {
            setValues(prev => ({
                ...prev,
                commercialUser: null
            }));
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier le détaillant' : 'Ajouter un détaillant'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom du détaillant</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <User size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        placeholder="Entrez le nom du détaillant"
                                        required
                                    />
                                </InputGroup>
                                {errors.name && <Form.Text className="text-danger">{errors.name}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Mail size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        placeholder="Entrez l'email du détaillant"
                                        required
                                    />
                                </InputGroup>
                                {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Téléphone</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Phone size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={values.phone}
                                        onChange={handleChange}
                                        placeholder="Entrez le numéro de téléphone"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Type d'activité</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <BookOpen size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="businessType"
                                        value={values.businessType}
                                        onChange={handleChange}
                                        placeholder="Ex: Boulangerie, Mercerie, etc."
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Adresse</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <MapPin size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                as="textarea"
                                name="address"
                                value={values.address}
                                onChange={handleChange}
                                placeholder="Entrez l'adresse complète"
                                style={{ height: '80px' }}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            {/* <Form.Group className="mb-3">
                                <Form.Label>Limite de crédit</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <DollarSign size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="creditLimit"
                                        value={values.creditLimit}
                                        onChange={handleChange}
                                        placeholder="Entrez la limite de crédit"
                                    />
                                </InputGroup>
                            </Form.Group> */}
                        </Col>

                        <Col md={6} className="d-flex align-items-center">
                            <Form.Group className="mb-3 mt-2">
                                <Form.Check
                                    type="checkbox"
                                    id="vatApplicable"
                                    name="vatApplicable"
                                    label="Soumis à la TVA"
                                    checked={values.vatApplicable}
                                    onChange={handleCheckboxChange}
                                />
                                <Form.Text className="text-muted">
                                    Cochez si ce détaillant est soumis à la TVA
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    {canAssignCommercial && (
                        <Form.Group className="mb-3">
                            <Form.Label>Commercial assigné</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>
                                    <Users size={18} />
                                </InputGroup.Text>
                                <Form.Select
                                    name="commercialUser"
                                    value={values.commercialUser ? values.commercialUser.id : ''}
                                    onChange={handleCommercialChange}
                                    disabled={loadingCommercials}
                                >
                                    <option value="">-- Aucun commercial assigné --</option>
                                    {commercials.map(commercial => (
                                        <option key={commercial.id} value={commercial.id}>
                                            {commercial.fullName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </InputGroup>
                            {loadingCommercials && (
                                <Form.Text className="text-muted">
                                    Chargement des commerciaux...
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="notes"
                            value={values.notes}
                            onChange={handleChange}
                            placeholder="Notes additionnelles..."
                            style={{ height: '100px' }}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditMode ? 'Mise à jour...' : 'Création...'}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Mettre à jour' : 'Créer'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default RetailerForm;